// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { isUndefined } from '@shared/utils';
import { useIlliquidAssetDrawer } from './use-illiquid-asset-drawer';
import { IlliquidAssetFinancialEventsSection } from './illiquid-asset-financial-events-section';
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ListItemProps } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { Controller } from 'react-hook-form';
import {
  Input,
  Label,
  OptionsSelector,
  DateInput,
} from '@shared/ui-kit/components';
import { NumericFormat } from 'react-number-format';
import drawerStyles from './illiquid-asset-drawer.module.css';

export const IlliquidAssetDrawer = defineComponent({
  displayName: 'IlliquidAssetDrawer',
  useSetup: useIlliquidAssetDrawer,
  Slots: {
    Title: ({ children }: PropsWithChildren) => (
      <Typography variant="subtitle2" className={drawerStyles['section-title']}>
        {children}
      </Typography>
    ),
    Section: ({
      divider,
      className,
      ...props
    }: ListItemProps & { divider?: boolean }) => (
      <>
        <ListItem
          className={classNames(drawerStyles.section, className)}
          disablePadding
          disableGutters
          {...props}
        >
          {props.children}
        </ListItem>
        {divider && <Divider />}
      </>
    ),
  },
  View: ({
    DrawerRenderer,
    onClose,
    t,
    Slots,
    form,
    isPeVc,
    category,
    assetTypeTiles,
    subcategoryOptions,
    onAssetTypeSelect,
    onSubmit,
    currencyOptions,
    draftEvents,
    onDraftEventsChange,
    draftDefaultCurrency,
    isCreating,
  }) => (
    <DrawerRenderer
      anchor="right"
      PaperProps={{
        classes: {
          root: drawerStyles['drawer-paper'],
        },
      }}
    >
      <List dense disablePadding className={drawerStyles['drawer-list']}>
        <Slots.Section className={drawerStyles['section-sticky-top']}>
          <div className={drawerStyles['header-row']}>
            <Typography variant="h6" className={drawerStyles['drawer-title']}>
              <Translation
                i18nKey="illiquid-asset-drawer.add-asset"
                ns="components"
              />
            </Typography>

            <IconButton
              aria-label={t('common.close', { ns: 'common' })}
              size="small"
              className={drawerStyles['close-button']}
              onClick={onClose}
              data-testid="illiquid-asset-drawer-close"
            >
              <AppIcon icon="Close" color="inherit" />
            </IconButton>
          </div>
        </Slots.Section>

        <Slots.Section divider>
          <Slots.Title>
            <Translation
              i18nKey="illiquid-asset-drawer.section.basic-information"
              ns="components"
            />
          </Slots.Title>

          <div className={drawerStyles['section-body']}>
            <fieldset className={drawerStyles['fieldset-stack']}>
              <Controller
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-name"
                      required
                      error={!!fieldState.error}
                    >
                      {isPeVc ? (
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.investment-name"
                          ns="components"
                        />
                      ) : (
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.asset-name"
                          ns="components"
                        />
                      )}
                    </Label>
                    <Input
                      id="illiquid-asset-name"
                      {...field}
                      placeholder={
                        isPeVc
                          ? t(
                              'illiquid-asset-drawer.fields.investment-name-placeholder',
                              {
                                ns: 'components',
                              },
                            )
                          : t(
                              'illiquid-asset-drawer.fields.asset-name-placeholder',
                              {
                                ns: 'components',
                              },
                            )
                      }
                      error={!!fieldState.error}
                    />
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="category"
                render={({ field }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label htmlFor="illiquid-asset-type">
                      <Translation
                        i18nKey="illiquid-asset-drawer.fields.asset-type"
                        ns="components"
                      />
                    </Label>
                    <div className={drawerStyles['tile-grid-5']}>
                      {assetTypeTiles.map(option => (
                        <Tooltip key={option.value} title={option.tooltip} arrow>
                          <button
                            type="button"
                            onClick={() => {
                              field.onChange(option.value);
                              onAssetTypeSelect(option.value);
                            }}
                            className={drawerStyles['asset-type-tile']}
                            aria-pressed={field.value === option.value}
                          >
                            <AppIcon icon={option.icon} color="inherit" size="lg" />
                            <span className={drawerStyles['tile-label']}>
                              {option.label}
                            </span>
                          </button>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="subcategory"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-subcategory"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-drawer.fields.subcategory"
                        ns="components"
                      />
                    </Label>
                    <OptionsSelector
                      optionsAvailable={subcategoryOptions}
                      onChangeValueSelected={val => field.onChange(val)}
                      optionSelected={field.value}
                      defaultText={t(
                        'illiquid-asset-drawer.fields.subcategory-placeholder',
                        { ns: 'components' },
                      )}
                      selectType="white"
                      error={!!fieldState.error}
                      required
                    />
                  </div>
                )}
              />

              {isPeVc && (
                <Controller
                  control={form.control}
                  name="fund_manager"
                  render={({ field, fieldState }) => (
                    <div className={drawerStyles['field-stack']}>
                      <Label
                        htmlFor="illiquid-asset-fund-manager"
                        required
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.fund-manager"
                          ns="components"
                        />
                      </Label>
                      <Input
                        id="illiquid-asset-fund-manager"
                        {...field}
                        placeholder={t(
                          'illiquid-asset-drawer.fields.fund-manager-placeholder',
                          { ns: 'components' },
                        )}
                        error={!!fieldState.error}
                      />
                    </div>
                  )}
                />
              )}

              <Controller
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-description"
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-drawer.fields.description"
                        ns="components"
                      />
                    </Label>
                    <textarea
                      id="illiquid-asset-description"
                      value={field.value ?? ''}
                      placeholder={t(
                        'illiquid-asset-drawer.fields.description-placeholder',
                        { ns: 'components' },
                      )}
                      onChange={e => field.onChange(e.target.value)}
                      className={classNames(
                        'input-common',
                        drawerStyles['textarea-field'],
                        fieldState.error && 'input-error',
                      )}
                    />
                  </div>
                )}
              />

              {isPeVc && (
                <Controller
                  control={form.control}
                  name="currency"
                  render={({ field, fieldState }) => (
                    <div className={drawerStyles['field-stack']}>
                      <Label
                        htmlFor="illiquid-asset-currency"
                        required
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.currency"
                          ns="components"
                        />
                      </Label>
                      <OptionsSelector
                        optionsAvailable={currencyOptions}
                        onChangeValueSelected={val => field.onChange(val)}
                        optionSelected={field.value}
                        defaultText="EUR"
                        defaultValue={field.value}
                        selectType="white"
                        error={!!fieldState.error}
                        required
                      />
                    </div>
                  )}
                />
              )}
            </fieldset>
          </div>
        </Slots.Section>

        <Slots.Section divider>
          <Slots.Title>
            {isPeVc ? (
              <Translation
                i18nKey="illiquid-asset-drawer.section.commitment"
                ns="components"
              />
            ) : (
              <Translation
                i18nKey="illiquid-asset-drawer.section.acquisition"
                ns="components"
              />
            )}
          </Slots.Title>

          <div className={drawerStyles['section-body']}>
            {isPeVc ? (
              <Controller
                control={form.control}
                name="total_commitment"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-commitment-total"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-drawer.fields.commitment-total"
                        ns="components"
                      />
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="illiquid-asset-commitment-total"
                      value={field.value ?? 0}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? 0)
                      }
                      onBlur={() => field.onBlur()}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={false}
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      thousandSeparator
                      className={classNames(
                        drawerStyles['numeric-input'],
                        fieldState.error ? 'input-error' : undefined,
                      )}
                    />
                  </div>
                )}
              />
            ) : (
              <fieldset className={drawerStyles['fieldset-stack']}>
                <Controller
                  control={form.control}
                  name="acquisition_date"
                  render={({ field, fieldState }) => (
                    <div className={drawerStyles['field-stack']}>
                      <Label
                        htmlFor="illiquid-asset-acquisition-date"
                        required
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.acquisition-date"
                          ns="components"
                        />
                      </Label>
                      <DateInput
                        id="illiquid-asset-acquisition-date"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        error={!!fieldState.error}
                      />
                    </div>
                  )}
                />

                <div className={drawerStyles['grid-two-cols']}>
                  <Controller
                    control={form.control}
                    name="acquisition_value"
                    render={({ field, fieldState }) => (
                      <div className={drawerStyles['field-stack']}>
                        <Label
                          htmlFor="illiquid-asset-acquisition-value"
                          required
                          error={!!fieldState.error}
                        >
                          <Translation
                            i18nKey="illiquid-asset-drawer.fields.acquisition-value"
                            ns="components"
                          />
                        </Label>
                        <NumericFormat
                          customInput={Input}
                          id="illiquid-asset-acquisition-value"
                          value={field.value ?? 0}
                          onValueChange={({ floatValue }) =>
                            field.onChange(floatValue ?? 0)
                          }
                          onBlur={() => field.onBlur()}
                          allowNegative={false}
                          decimalScale={2}
                          fixedDecimalScale={false}
                          allowedDecimalSeparators={DECIMAL_SEPARATORS}
                          thousandSeparator
                          isAllowed={({ floatValue }) =>
                            floatValue === undefined || floatValue > 0
                          }
                          className={classNames(
                            drawerStyles['numeric-input'],
                            fieldState.error ? 'input-error' : undefined,
                          )}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    control={form.control}
                    name="currency"
                    render={({ field, fieldState }) => (
                      <div className={drawerStyles['field-stack']}>
                        <Label
                          htmlFor="illiquid-asset-currency"
                          required
                          error={!!fieldState.error}
                        >
                          <Translation
                            i18nKey="illiquid-asset-drawer.fields.currency"
                            ns="components"
                          />
                        </Label>
                        <OptionsSelector
                          optionsAvailable={currencyOptions}
                          onChangeValueSelected={val => field.onChange(val)}
                          optionSelected={field.value}
                          defaultText="EUR"
                          defaultValue={field.value}
                          selectType="white"
                          error={!!fieldState.error}
                          required
                        />
                      </div>
                    )}
                  />
                </div>

                <Controller
                  control={form.control}
                  name="ownership_pct"
                  render={({ field, fieldState }) => (
                    <div className={drawerStyles['field-stack']}>
                      <Label
                        htmlFor="illiquid-asset-ownership-percent"
                        required
                        error={!!fieldState.error}
                      >
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.ownership-percent"
                          ns="components"
                        />
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        id="illiquid-asset-ownership-percent"
                        value={(field.value ?? 0) * 100}
                        onValueChange={({ floatValue }) =>
                          field.onChange((floatValue ?? 0) / 100)
                        }
                        onBlur={() => field.onBlur()}
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale={false}
                        allowedDecimalSeparators={DECIMAL_SEPARATORS}
                        suffix="%"
                        isAllowed={({ floatValue }) =>
                          isUndefined(floatValue) ||
                          (floatValue >= 0 && floatValue <= 100)
                        }
                        className={classNames(
                          drawerStyles['numeric-input'],
                          fieldState.error ? 'input-error' : undefined,
                        )}
                      />
                    </div>
                  )}
                />
              </fieldset>
            )}
          </div>
        </Slots.Section>

        <Slots.Section divider>
          <Slots.Title>
            <Translation
              i18nKey="illiquid-asset-drawer.section.current-valuation"
              ns="components"
            />
          </Slots.Title>

          <div className={drawerStyles['section-body']}>
            <div className={drawerStyles['grid-two-cols']}>
              <Controller
                control={form.control}
                name="current_valuation"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-estimated-current-value"
                      required
                      error={!!fieldState.error}
                    >
                      {isPeVc ? (
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.valuation-nav-value"
                          ns="components"
                        />
                      ) : (
                        <Translation
                          i18nKey="illiquid-asset-drawer.fields.valuation-value"
                          ns="components"
                        />
                      )}
                    </Label>
                    <NumericFormat
                      customInput={Input}
                      id="illiquid-asset-estimated-current-value"
                      value={field.value ?? 0}
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? 0)
                      }
                      onBlur={() => field.onBlur()}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={false}
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      thousandSeparator
                      placeholder={t(
                        'illiquid-asset-drawer.fields.valuation-value-placeholder',
                        { ns: 'components' },
                      )}
                      isAllowed={({ floatValue }) =>
                        isUndefined(floatValue) || floatValue > 0
                      }
                      className={classNames(
                        drawerStyles['numeric-input'],
                        fieldState.error ? 'input-error' : undefined,
                      )}
                    />
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="valuation_date"
                render={({ field, fieldState }) => (
                  <div className={drawerStyles['field-stack']}>
                    <Label
                      htmlFor="illiquid-asset-valuation-date"
                      required
                      error={!!fieldState.error}
                    >
                      <Translation
                        i18nKey="illiquid-asset-drawer.fields.valuation-date"
                        ns="components"
                      />
                    </Label>
                    <DateInput
                      id="illiquid-asset-valuation-date"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      error={!!fieldState.error}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </Slots.Section>

        <Slots.Section divider>
          <IlliquidAssetFinancialEventsSection
            mode="draft"
            draftEvents={draftEvents}
            onDraftEventsChange={onDraftEventsChange}
            defaultCurrency={draftDefaultCurrency}
          />
        </Slots.Section>

        <Slots.Section divider className={drawerStyles['section-spacer']} />

        <Slots.Section className={drawerStyles['section-sticky-bottom']}>
          <div className={drawerStyles['footer-actions']}>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              className={drawerStyles['footer-actions-button']}
              onClick={onClose}
              disabled={isCreating}
            >
              <Translation i18nKey="common.cancel" ns="common" />
            </Button>

            <Button
              type="button"
              variant="contained"
              color="secondary"
              className={drawerStyles['footer-actions-button']}
              disabled={
                !form.formState.isValid || form.formState.isSubmitting || isCreating
              }
              onClick={form.handleSubmit(onSubmit)}
              startIcon={
                isCreating || form.formState.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              <Translation
                i18nKey="illiquid-asset-drawer.add-asset"
                ns="components"
              />
            </Button>
          </div>
        </Slots.Section>
      </List>
    </DrawerRenderer>
  ),
});
